import { Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import { t } from "i18next";

import { PlusIcon } from "@heroicons/react/20/solid";

import ColumnActionsInventory from "../molecules/ColumnActionsInventory";
import { ButtonSecondary } from "../atoms/ButtonSecondary";
import { TableLoader } from "../molecules/TableLoader";
import ColumnText from "../molecules/ColumnText";
import { Select } from "./Select";
import { Empty } from "./Empty";

type InventarioProps = {
  id: number;
  item: string;
  referencia: string;
  cate: string;
  goria: string;
  name: string;
  nro: number;
};

type OptionCategoryProps = {
  id: number;
  name: string;
};

export type TableListInventoryProps = {
  isLoading: boolean;
  inventarios: InventarioProps[];
  title: string;
  description: string;
  functionButtonCreate: () => void;
  filterButton: () => void;
  functionButtonCreateCategory: () => void;
  optionsCategory: OptionCategoryProps[];
  category: OptionCategoryProps;
  setCategory: (option: OptionCategoryProps) => void;
  onEdit: (inventoryItem: any) => void;
  onDelete: (inventoryItem: any) => void;
  onViewTasks: (inventoryItem: any) => void;
  canCreateInventory: boolean;
  canEditInventory: boolean;
  canDeleteInventory: boolean;
};

const TableListInventory: React.FC<TableListInventoryProps> = ({
  isLoading,
  title,
  description,
  inventarios,
  functionButtonCreate,
  functionButtonCreateCategory,
  optionsCategory,
  category,
  setCategory,
  onEdit,
  onDelete,
  onViewTasks,
  canCreateInventory,
  canEditInventory,
  canDeleteInventory,
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  return (
    <div className="flex-1 px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center justify-between">
        <div className="flex-grow">
          <h1 className="text-base font-semibold leading-6 text-gray-900">
            {title}
          </h1>
          <p className="mt-2 text-sm text-gray-700">{description}</p>
        </div>
        {canCreateInventory && (
          <div className="mt-4 sm:mt-0">
            <button
              onClick={functionButtonCreate}
              type="button"
              className="block rounded-full border-2 border-dashed border-gray-400 p-1 text-gray-400 shadow-sm hover:text-gray-500 focus-visible:outline-none"
            >
              <PlusIcon aria-hidden="true" className="h-5 w-5" />
            </button>
          </div>
        )}
      </div>

      {/* Divider debajo del texto */}
      <div className="border-b border-gray-300 my-3"></div>

      <div className="sm:flex sm:items-center">
        <div className="sm:flex sm:flex-col w-64">
          <label className="text-sm font-medium text-gray-900"></label>
          <Select
            name="category"
            options={optionsCategory}
            selected={category}
            setSelected={setCategory}
            tittle={t("general.search")}
          />
        </div>
        <div className="mt-8 sm:ml-auto sm:mt-4 sm:flex sm:items-center sm:space-x-4">
          <div className="sm: mb-5 md:mb-0">
            <ButtonSecondary
              text={t("inventory.views.create_category")}
              onClick={functionButtonCreateCategory}
              size="large"
            />
          </div>
          <div className="sm: mb-5 md:mb-0">
            <ButtonSecondary
              text={t("maintenance.views.title")}
              onClick={() => navigate(`/branchs/${id}/maintenance`)}
              size="large"
            />
          </div>
        </div>
      </div>

      <div className="border-b border-gray-300 mt-5"></div>
      <div className="flow-root">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full px-3 py-2 align-middle sm:px-6 lg:px-8">
            {isLoading ? (
              <TableLoader columns={3} />
            ) : (
              <Fragment>
                {inventarios.length === 0 ? (
                  <div className="pt-5">
                    <Empty
                      texto={t("inventory.views.empty")}
                      onClick={functionButtonCreate}
                    />
                  </div>
                ) : (
                  <Fragment>
                    <table className="min-w-full divide-y divide-gray-300">
                      <thead>
                        <tr>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                          >
                            {t("inventory.views.item")}
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                          >
                            {t("inventory.views.reference")}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {inventarios.map((inventario) => (
                          <tr key={inventario.id}>
                            <ColumnText title={inventario.name} />
                            <ColumnText title={inventario.nro} />
                            <ColumnActionsInventory
                              name={inventario.item}
                              onEdit={() => onEdit(inventario)}
                              onDelete={() => onDelete(inventario)}
                              onViewTasks={() => onViewTasks(inventario)}
                              canEditInventory={canEditInventory}
                              canDeleteInventory={canDeleteInventory}
                            />
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Fragment>
                )}
              </Fragment>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TableListInventory;
